import React, { useContext } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { useDispatch } from 'react-redux';
import { DELETE_DISCIPLINE } from '../../Reducers/Actions';
import { useDeleteDisciplineMutation } from '../../services/SkillsManagement';
import MuiDialog from '../../SystemDesign/MuiDialog';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';

const DeleteDiscipline = ({ open, onClose, discipline, onDeleted }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  
  // RTK Query deletion hook
  const [deleteDisciplineMutation, { isLoading }] = useDeleteDisciplineMutation();
  
  // Access the SnackBar context
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  
  // Handle discipline deletion
  const handleDelete = async () => {
    if (!discipline) return;
    
    try {
      // Call API to delete the discipline
      await deleteDisciplineMutation({
        disciplineId: discipline.discipline_id
      }).unwrap();
      
      // Remove color from localStorage
      try {
        const disciplineColors = JSON.parse(localStorage.getItem('disciplineColors') || '{}');
        delete disciplineColors[discipline.discipline_id];
        localStorage.setItem('disciplineColors', JSON.stringify(disciplineColors));
      } catch (error) {
        console.error('Error removing color from localStorage:', error);
        // Non-critical error, continue with deletion
      }
      
      // Update Redux store
      dispatch({ type: DELETE_DISCIPLINE, payload: discipline.discipline_id });
      
      // Show success message
      setInfoAlert({
        severity: 'success',
        message: `Department "${discipline.name || 'Unknown'}" successfully deleted`
      });
      setOpenSnackBar(true);
      
      // Call the onDeleted callback if provided
      if (onDeleted) {
        onDeleted(discipline.discipline_id);
      }
      
      // Close the dialog
      onClose();
    } catch (error) {
      console.error('Failed to delete discipline:', error);
      
      // Show error message
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'Failed to delete department'
      });
      setOpenSnackBar(true);
    }
  };
  
  // No need for custom button content, using MuiDialog's built-in action button

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      icon={<DeleteOutlineIcon />}
      title="Delete Department?"
      subtitle={`Are you sure you want to delete "${discipline?.name}"? This action cannot be undone.`}
      maxWidth="xs"
      existAction={true}
      actionTitle="Delete"
      actionFunction={handleDelete}
      isLoading={isLoading}
      actionType="button"
    />
  );
};

export default DeleteDiscipline;